<template>
  <v-layout row>
    <v-flex xs12 :md10="!detailedView" :offset-md1="!detailedView">
      <v-layout row wrap>
        <v-flex xs12 sm4>
          <v-select label="Search by" :items="userTypeOptions" :value="userType"
                    @input="selectUserType"/>
        </v-flex>
        <v-flex xs12 sm6 offset-sm2>
          <single-select-search :value="userId" @input="selectUser" :options="users"
                                keypath="aoid" labelpath="name"/>
        </v-flex>
      </v-layout>
      <v-layout row wrap>
        <v-flex xs12>
          <v-btn small :depressed="includePending" :dark="includePending" :flat="!includePending"
                 @click="includePending = !includePending">
            Pending
          </v-btn>
          <v-btn small color="success" :depressed="includeApproved" :flat="!includeApproved"
                 @click="includeApproved = !includeApproved">
            Approved
          </v-btn>
          <v-btn small color="error" :depressed="includeRejected" :flat="!includeRejected"
                 @click="includeRejected = !includeRejected">
            Rejected
          </v-btn>
          <v-btn small color="warning" :depressed="includeCancelled" :flat="!includeCancelled"
                 @click="includeCancelled = !includeCancelled">
            Cancelled
          </v-btn>
        </v-flex>
      </v-layout>
      <v-layout row>
        <v-flex xs12>
          <dynamo-table class="edval-list" :headers="headers" :items="requests"
                        :disable-initial-sort="true" :pagination="pagination"
                        @update:pagination="getData">
            <template slot="default" slot-scope="request">
              <td>
                <v-icon v-if="request.item.status !== 'PENDING'"
                        :color="request.item.statusColour"
                        small>fiber_manual_record
                </v-icon>
              </td>
              <td>{{ request.item.user }}</td>
              <td>{{ request.item.created}}</td>
              <td>{{ request.item.closedBy }}</td>
              <td>{{ request.item.closed }}</td>
              <td>
                <v-btn flat small icon
                       :to="`/approvals/requests/${request.item.userId}/${request.item.createdTs}`">
                  <v-icon>visibility</v-icon>
                </v-btn>
              </td>
              <td v-if="detailedView">
                <yaml-representation v-model="request.item.scopes"/>
              </td>
            </template>
          </dynamo-table>
        </v-flex>
      </v-layout>
      <v-layout row>
        <v-flex xs12 class="buffer">
          <v-checkbox v-model="detailedView" label="Show details"/>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import { mapParams } from '@/util';
  import SingleSelectSearch from '@/views/shared/SingleSelectSearch';
  import YamlRepresentation from '@/views/shared/yaml/YamlRepresentation';
  import DynamoTable from '@/views/shared/DynamoTable';
  import mapStatus from './mapStatus';

  export default {
    components: { SingleSelectSearch, YamlRepresentation, DynamoTable },
    props: {
      status: {
        type: String,
        default: 'PENDING,APPROVED,REJECTED,CANCELLED'
      }
    },
    data() {
      return {
        headers: [
          { text: 'Status' },
          { text: 'Created by' },
          { text: 'Created date' },
          { text: 'Closed by' },
          { text: 'Closed date' }
        ],
        requests: [],
        userTypeOptions: [
          { text: 'Requestor', value: 'user' },
          { text: 'Approver', value: 'approver' }
        ],
        includeApproved: true,
        includeRejected: true,
        includePending: true,
        includeCancelled: true,
        detailedView: false
      };
    },
    computed: {
      userType() {
        return this.userTypeQs || 'user';
      },
      queryStatus() {
        const status = [];
        if (this.includePending) {
          status.push('PENDING');
        }
        if (this.includeApproved) {
          status.push('APPROVED');
        }
        if (this.includeRejected) {
          status.push('REJECTED');
        }
        if (this.includeCancelled) {
          status.push('CANCELLED');
        }

        return status.join(',');
      },
      queryParams() {
        const params = {
          status: this.queryStatus
        };
        params[this.userType] = this.userId;

        return params;
      },
      ...mapState('auth', [
        'user',
        'groups'
      ]),
      ...mapState('approvals', {
        pagination: 'requestPagination',
        users: 'flowMembers'
      }),
      ...mapParams({
        userId: 'userId',
        userTypeQs: 'userType'
      })
    },
    watch: {
      queryParams() {
        this.updateView();
      }
    },
    methods: {
      selectUserType(userType) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            userType
          }
        });
      },
      selectUser(userId) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            userId
          }
        });
      },
      getData(pagination) {
        if (!this.userId) {
          return this.setPagination(pagination).catch(this.$defaultHandler);
        }

        const params = {
          ...this.queryParams,
          size: pagination.rowsPerPage
        };
        if (pagination.exclusiveStartKeys.length > 0) {
          const lastIndex = pagination.exclusiveStartKeys.length - 1;
          params.exclusiveStartKey = pagination.exclusiveStartKeys[lastIndex];
        }

        return this.$services.authenticator.get('/approvals/requests', { params })
          .then((response) => {
            this.requests = response.data.data
              .map((r) => {
                return {
                  ...r,
                  userId: r.user.aoid,
                  statusColour: mapStatus(r.status),
                  user: r.user.name,
                  created: new Date(r.created).toLocaleString('en-AU'),
                  closedBy: r.closedBy ? r.closedBy.name : '',
                  closed: r.closed ? new Date(r.closed).toLocaleString('en-AU') : ''
                };
              });

            const lastEvaluatedKey = response.data.lastPage ? null : response.data.lastEvaluatedKey;
            return this.setPagination({
              ...pagination,
              lastEvaluatedKey
            });
          }).catch(this.$defaultHandlerHttp);
      },
      updateView() {
        this.getData(this.pagination);
      },
      ...mapActions('users', [
        'initialize'
      ]),
      ...mapActions('approvals', {
        setPagination: 'setRequestPagination',
        initialize: 'initializeFlowMembers'
      })
    },
    mounted() {
      const status = this.status.split(',');
      this.includePending = status.includes('PENDING');
      this.includeApproved = status.includes('APPROVED');
      this.includeRejected = status.includes('REJECTED');
      this.includeCancelled = status.includes('CANCELLED');

      if (!this.pagination.exclusiveStartKeys || this.pagination.exclusiveStartKeys.length < 1) {
        this.getData({
          ...this.pagination,
          exclusiveStartKeys: []
        });
      } else {
        this.updateView();
      }
      this.initialize().catch(this.$defaultHandlerHttp);
    }
  };
</script>

<style lang="less" scoped>
  @import '../../style/list.less';

  .buffer {
    margin-top: 10px;
  }
</style>
